import React from 'react';
import { Card, Skeleton, Tooltip, Typography, theme } from 'antd';
import { InboxOutlined, WarningOutlined } from '@ant-design/icons';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { formatNumber, formatDuration } from '@leedpulse-monorepo/utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';
import type { ContactsQueueStats } from '../../../../domain/use_cases/leedflow/overview/getContactsQueueStats';

dayjs.extend(relativeTime);
dayjs.locale('fr');

const { useToken } = theme;

interface ContactsQueueCardProps {
  stats: ContactsQueueStats;
  status: ContextStatus;
}

// Santé de la queue d'import de contacts (`ContactsQueue`), tous
// users/companies confondus. Rafraîchi toutes les 30 s par `useContactsQueueStats`.
const ContactsQueueCard: React.FC<ContactsQueueCardProps> = ({ stats, status }) => {
  const { token } = useToken();

  if (status === ContextStatus.ERROR) return null;

  const oldestPending = stats.oldestPendingCreated !== null ? dayjs(stats.oldestPendingCreated) : null;

  return (
    <Card title="Import des contacts">
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={false} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
          <Tooltip title="Contacts en attente de traitement dans la queue d'import.">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: token.colorTextSecondary }}>
                <InboxOutlined />
              </span>
              <span style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {formatNumber(stats.pending)}
              </span>
              <Typography.Text type="secondary">en attente</Typography.Text>
            </span>
          </Tooltip>

          {stats.pending > 0 && (
            <Tooltip title="Estimation théorique basée sur le débit du job (100 contacts/minute), pas sur un débit mesuré.">
              <Typography.Text type="secondary">
                Temps estimé : {formatDuration(stats.estimatedMinutesRemaining * 60_000)}
              </Typography.Text>
            </Tooltip>
          )}

          <Tooltip title="Contacts en échec (statuts failed et error).">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: stats.failed > 0 ? token.colorWarning : token.colorTextSecondary }}>
                <WarningOutlined />
              </span>
              <span style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {formatNumber(stats.failed)}
              </span>
              <Typography.Text type="secondary">échecs</Typography.Text>
            </span>
          </Tooltip>

          {oldestPending && (
            <Tooltip title={oldestPending.format('DD/MM/YYYY HH:mm:ss')}>
              <Typography.Text type="secondary">
                Le plus vieux en attente : {oldestPending.fromNow(true)}
              </Typography.Text>
            </Tooltip>
          )}
        </div>
      )}
    </Card>
  );
};

export default ContactsQueueCard;
